import React, { Fragment } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  DatePicker,
  FileUpload,
  SalvDropdown,
  SalvFieldHeader,
  SalvNumberField,
  SalvRadio,
  SalvRadioGroup,
  SalvTextArea,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import dayjs from 'dayjs';
import {
  BridgeApiAttachment,
  BridgeApiConversationCounterparty,
  BridgeApiTemplate,
  BridgeApiTemplateField,
  BridgeApiTemplateFieldTypeEnum,
  InternalApiAlertRfi,
  InternalApiAlertRfiMessage,
  InternalApiAlertRfiMessageType,
  InternalApiManualAlert,
  InternalApiMonitoringAlert,
} from '../api-internal';
import { ApiBridgeAlert, ApiMatchSourceType, ApiSanctionAlert } from '../api';
import { attachmentApi } from '../apis';
import { faPaperclipVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { AnySchema } from 'yup';
import { matchSourceColor } from '../screening/alert/matches/helper';
import { getHighlightedValue } from '../aml/sections/attributes/highlighters';

export interface MessengerTemplateFieldConfiguration {
  show_when?: MessengerTemplateFieldShowWhenConfiguration;
  boolean_display_value?: MessengerTemplateFieldBooleanValueConfiguration;
  notRelevantEnabled?: boolean;
  defaultValue?: boolean | string;
  configurations?: MappingConfiguration[];
  repeatable?: boolean;
  requestedField?: MessengerTemplateFieldRequestedFieldConfiguration;
  select_options?: string[];
  isMulti?: boolean;
}

export interface MessengerTemplateFieldShowWhenConfiguration {
  field_id: string;
  field_value: boolean | string;
}

export interface MessengerTemplateFieldRequestedFieldConfiguration {
  toggledByDefault: boolean;
}

export interface MessengerTemplateFieldBooleanValueConfiguration {
  true_value: string;
  false_value: string;
}

interface MappingConfiguration {
  conditions: MappingConfigurationCondition[] | null;
  fields: string[];
}

interface MappingConfigurationCondition {
  fieldName: string;
  type: string;
  value: string;
}

export interface DecryptedAttachment extends BridgeApiAttachment {
  decryptedFileName?: string;
}

export interface DecryptedMessage extends InternalApiAlertRfiMessage {
  decryptedContent?: string;
  decryptedAttachments?: Array<DecryptedAttachment>;
}

export function counterpartiesNames(conversation: InternalApiAlertRfi): string {
  const friends = conversation.counterparties
    .filter((counterparty) => counterparty.organisationId)
    .map((counterparty) => counterparty.organisationName);

  const others = conversation.counterparties.filter((counterparty) => !counterparty.organisationId);

  return (
    friends.join(', ') +
    (others.length > 0
      ? others.length === 1
        ? ` and ${others.length} other organisation`
        : ` and ${others.length} other organisations`
      : '')
  );
}

export function getMessengerFieldConfiguration(field: BridgeApiTemplateField): MessengerTemplateFieldConfiguration {
  try {
    return JSON.parse(field?._configuration || '');
  } catch {
    return {};
  }
}

function isDefaultValueValid(value: any, field: BridgeApiTemplateField): boolean {
  if (value == undefined) return false;

  switch (field.type) {
    case BridgeApiTemplateFieldTypeEnum.BOOLEAN:
      return typeof value === 'boolean';
    case BridgeApiTemplateFieldTypeEnum.TEXT:
    case BridgeApiTemplateFieldTypeEnum.TEXTAREA:
      return typeof value === 'string';
    case BridgeApiTemplateFieldTypeEnum.DATE:
      return dayjs(value, 'YYYY-MM-DD').isValid();
    case BridgeApiTemplateFieldTypeEnum.NUMBER:
      return typeof value === 'number';
    case BridgeApiTemplateFieldTypeEnum.SELECT:
      return getMessengerFieldConfiguration(field).isMulti ? Array.isArray(value) : typeof value === 'string';
    default:
      return false;
  }
}

export function getDefaultValue(field: BridgeApiTemplateField): any {
  const defaultValue = getMessengerFieldConfiguration(field).defaultValue;

  if (isDefaultValueValid(defaultValue, field)) {
    return defaultValue;
  }

  if (field.type === BridgeApiTemplateFieldTypeEnum.FILE) {
    return [];
  } else if (field.type === BridgeApiTemplateFieldTypeEnum.NUMBER) {
    return null;
  }

  return '';
}

export function getRequestedFieldToggledByDefaultValue(field: BridgeApiTemplateField): boolean {
  const requestedFieldConfiguration = getMessengerFieldConfiguration(field).requestedField;

  if (requestedFieldConfiguration) {
    return requestedFieldConfiguration.toggledByDefault;
  }

  return false;
}

export const INPUT_REGEX = new RegExp(/^.*<(“[^”]*”|'[^’]*’|[^'”>])*>.*$/);

export const testInput = (value: string | undefined): boolean => {
  if (value) {
    return !INPUT_REGEX.test(value);
  }
  return true;
};

export function setRequired(schema: AnySchema, field: BridgeApiTemplateField): AnySchema {
  const isListField =
    field.type === BridgeApiTemplateFieldTypeEnum.FILE ||
    (field.type === BridgeApiTemplateFieldTypeEnum.SELECT && getMessengerFieldConfiguration(field).isMulti);

  if (isListField && field.required) {
    return (schema as Yup.ArraySchema<any[], any>).min(1);
  }
  if (field.required) {
    return schema.required('Required');
  } else {
    return schema;
  }
}

export const isoDateFormatRegex = new RegExp('^\\d{4}-\\d{2}-\\d{2}$');

export function getSchemaType(field: BridgeApiTemplateField): AnySchema {
  switch (field.type) {
    case BridgeApiTemplateFieldTypeEnum.NUMBER:
      return Yup.number().nullable();
    case BridgeApiTemplateFieldTypeEnum.TEXT:
    case BridgeApiTemplateFieldTypeEnum.TEXTAREA:
      return Yup.string().test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value));
    case BridgeApiTemplateFieldTypeEnum.DATE:
      return Yup.string()
        .matches(isoDateFormatRegex, MESSENGER_DEFAULT_DATE_FORMAT)
        .test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value));
    case BridgeApiTemplateFieldTypeEnum.BOOLEAN:
      return Yup.boolean().nullable();
    case BridgeApiTemplateFieldTypeEnum.FILE:
      return Yup.array().of(Yup.mixed());
    case BridgeApiTemplateFieldTypeEnum.SELECT:
      return getMessengerFieldConfiguration(field).isMulti ? Yup.array().of(Yup.string()) : Yup.string();
  }
}

export const getProvidedFieldValue = (
  alert: ApiSanctionAlert | InternalApiMonitoringAlert | ApiBridgeAlert | InternalApiManualAlert,
  field: BridgeApiTemplateField
): string => {
  const mappingConfigurations = getMessengerFieldConfiguration(field).configurations;

  if (!mappingConfigurations) {
    return '';
  }

  for (const configuration of mappingConfigurations) {
    let matchedConditions = configuration.conditions == null;

    if (configuration.conditions) {
      matchedConditions = true;
      for (const condition of configuration.conditions) {
        let matchedCondition = false;
        const realValue = getFieldValue(condition.fieldName, alert);

        if (realValue) {
          if (condition.type === 'EQUALS') {
            matchedCondition = realValue == condition.value;
          } else if (condition.type === 'STARTS_WITH') {
            matchedCondition = realValue.startsWith(condition.value);
          }
        }

        if (!matchedCondition) {
          matchedConditions = false;
          break;
        }
      }
    }

    if (matchedConditions) {
      const fieldValue = configuration.fields
        .map((field) => getFieldValue(field, alert))
        .filter((value) => value)
        .join(' ');

      if (fieldValue && fieldValue.trim() !== '') {
        if (field.type === BridgeApiTemplateFieldTypeEnum.DATE) {
          return dayjs(fieldValue).format(MESSENGER_DEFAULT_DATE_FORMAT);
        }

        return fieldValue;
      }
    }
  }

  return '';
};

export const getFieldValue = (path: string, alert: any): string => {
  const split = path.split('.');

  return (
    split.reduce((acc, name) => acc && acc[name], alert) ||
    ['entity', ...split].reduce((acc, name) => acc && acc[name], alert) ||
    ''
  );
};

export const MESSENGER_DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export function getField(
  templateField: BridgeApiTemplateField,
  value: any,
  isInvalid: boolean,
  onChange: (val: any) => void,
  size: Extract<Size, Size.EXTRA_SMALL | Size.SMALL | Size.MEDIUM> = Size.MEDIUM,
  withLabel: boolean = false,
  className: string = ''
) {
  switch (templateField.type) {
    case BridgeApiTemplateFieldTypeEnum.DATE: {
      const sizeString = size === Size.EXTRA_SMALL || size === Size.SMALL ? 'small' : 'medium';

      const field = (
        <DatePicker
          key={templateField.name}
          size={sizeString}
          showDefaultOptions={false}
          value={
            dayjs(value, MESSENGER_DEFAULT_DATE_FORMAT).isValid() ? dayjs(value, MESSENGER_DEFAULT_DATE_FORMAT) : null
          }
          onChange={(val) => {
            onChange(val ? val.format(MESSENGER_DEFAULT_DATE_FORMAT) : undefined);
          }}
          isInvalid={isInvalid}
        />
      );
      if (withLabel) {
        return (
          <div className={className} key={templateField.name}>
            <SalvFieldHeader label={templateField.displayName} required={templateField.required} size={size} />
            {field}
          </div>
        );
      } else {
        return field;
      }
    }
    case BridgeApiTemplateFieldTypeEnum.BOOLEAN: {
      const notRelevantEnabled = getMessengerFieldConfiguration(templateField).notRelevantEnabled;
      return (
        <SalvRadioGroup
          className={className}
          key={templateField.name}
          ariaLabel={templateField.displayName}
          label={withLabel ? templateField.displayName : undefined}
          direction='horizontal'
          value={value}
          onChange={(value: string) => {
            if (notRelevantEnabled && value === 'null') {
              onChange(null);
            } else {
              onChange(value === 'true');
            }
          }}
          required={templateField.required}
          size={size}
        >
          <SalvRadio invalid={isInvalid} label={getBooleanFieldValueDisplayName(true, templateField)} value={true} />
          <SalvRadio invalid={isInvalid} label={getBooleanFieldValueDisplayName(false, templateField)} value={false} />
          {notRelevantEnabled && <SalvRadio invalid={isInvalid} label='Not relevant' value={null} />}
        </SalvRadioGroup>
      );
    }
    case BridgeApiTemplateFieldTypeEnum.FILE: {
      const field = (
        <div className={isInvalid ? 'invalid' : ''}>
          <FileUpload key={templateField.name} onChange={onChange} size={size} isMulti />
        </div>
      );

      if (withLabel) {
        return (
          <div className={className} key={templateField.name}>
            <SalvFieldHeader label={templateField.displayName} required={templateField.required} size={size} />
            {field}
          </div>
        );
      } else {
        return field;
      }
    }
    case BridgeApiTemplateFieldTypeEnum.TEXTAREA:
      return (
        <SalvTextArea
          key={templateField.name}
          className={className}
          onChange={onChange}
          value={value}
          ariaLabel={templateField.displayName}
          label={withLabel ? templateField.displayName : undefined}
          autoSizing={true}
          size={size}
          isInvalid={isInvalid}
          maxRows={10}
          placeholder={templateField.placeholder}
          required={templateField.required}
        />
      );
    case BridgeApiTemplateFieldTypeEnum.NUMBER:
      return (
        <SalvNumberField
          key={templateField.name}
          className={className}
          onChange={onChange}
          value={value}
          ariaLabel={templateField.displayName}
          label={withLabel ? templateField.displayName : undefined}
          size={size}
          isInvalid={isInvalid}
          placeholder={templateField.placeholder}
          required={templateField.required}
        />
      );
    case BridgeApiTemplateFieldTypeEnum.TEXT:
    default:
      return (
        <SalvTextField
          key={templateField.name}
          className={
            size === Size.SMALL ? `text-s${withLabel ? ` ${className}` : ''}` : withLabel ? ` ${className}` : ''
          }
          onChange={onChange}
          value={value}
          ariaLabel={templateField.displayName}
          label={withLabel ? templateField.displayName : undefined}
          size={size}
          isInvalid={isInvalid}
          placeholder={templateField.placeholder}
          required={templateField.required}
        />
      );

    case BridgeApiTemplateFieldTypeEnum.SELECT: {
      const config = getMessengerFieldConfiguration(templateField);

      return (
        <SalvDropdown
          key={templateField.name}
          isInvalid={isInvalid}
          required={templateField.required}
          ariaLabel={templateField.displayName}
          label={withLabel ? templateField.displayName : ''}
          onChange={(options) => onChange(config.isMulti ? options.map((o) => o.value) : options[0]?.value)}
          options={[...new Set(config.select_options)].map((option) => ({
            value: option.toString(),
            label: option.toString(),
            key: option.toString(),
          }))}
          isMulti={config.isMulti}
          value={
            config.isMulti
              ? (value || []).map((option: string) => ({
                  value: option,
                  label: option,
                  key: option,
                }))
              : [
                  {
                    value,
                    label: value,
                    key: value,
                  },
                ]
          }
          className='m-b-8'
        />
      );
    }
  }
}

export function getBooleanFieldValueDisplayName(value: boolean, templateField: BridgeApiTemplateField): string {
  const boolConf = getMessengerFieldConfiguration(templateField).boolean_display_value;
  if (value) {
    return boolConf ? boolConf.true_value : 'Yes';
  } else {
    return boolConf ? boolConf.false_value : 'No';
  }
}

export const getTemplateFieldDisplayMappings = (fields: any[]): Record<string, any> => {
  return fields.reduce(
    (obj, templateField) =>
      Object.assign(obj, {
        [templateField.name]: templateField,
      }),
    {}
  );
};

export const getRequestedFieldsDisplayValue = (
  field: string,
  value: any,
  fieldMappings: BridgeApiTemplateField[],
  rfiSource: string,
  includeSeparator: boolean,
  highlight: boolean
) => {
  const displayField = fieldMappings?.find((requestedField) => requestedField.name === field)?.displayName;

  try {
    value = JSON.parse(value);
    value = getDisplayValue(value, field);
  } catch (_) {
    // ignore
  }
  return (
    <Fragment key={field}>
      <Row className='align-items-center'>
        <Col>
          <h5 className='mb-1'>{displayField ? displayField : field}</h5>
        </Col>
        <Col md='auto'>
          <span>
            {highlight
              ? getHighlightedValue({ text: value, highlight: value, color: matchSourceColor(ApiMatchSourceType.RFI) })
              : value}
          </span>
        </Col>
      </Row>
      <Row className='align-items-center'>
        <Col>
          <h5 className='text-muted mb-0'>Source: RFI to {rfiSource}</h5>
        </Col>
      </Row>
      {includeSeparator && <hr />}
    </Fragment>
  );
};

export function getDisplayValue(value: any, templateField?: any): any {
  if (templateField && templateField.type === BridgeApiTemplateFieldTypeEnum.BOOLEAN) {
    return getBooleanFieldValueDisplayName(value, templateField);
  } else if (templateField && templateField.type === BridgeApiTemplateFieldTypeEnum.SELECT) {
    return getMessengerFieldConfiguration(templateField).isMulti ? value.join(', ') : value;
  } else if ((value as JsonMessageFileValue).id && (value as JsonMessageFileValue).fileName) {
    return (
      <div
        className='attachment btn-link'
        onClick={() => onDownloadAttachment(value.id, value.fileName)}
        key={value.id}
      >
        <FontAwesomeIcon icon={faPaperclipVertical} className='fe me-2' />
        {value.fileName}
      </div>
    );
  } else if (Array.isArray(value)) {
    return Object.values(value).map((arrValue: any, index) => {
      const isFile = arrValue?.fileName || arrValue instanceof File;
      return (
        <React.Fragment key={index}>
          {/* Separate list fields with comma except for files */}
          {!!index && !isFile && <span>, </span>}
          {getDisplayValue(arrValue, templateField)}
        </React.Fragment>
      );
    });
  }
  // for request preview
  else if (value instanceof File) {
    return <div>{value.name}</div>;
  } else {
    return value.toString();
  }
}

export const getProvidedFieldsDisplayValue = (field: any, value: any, rfiTemplate: BridgeApiTemplate | undefined) => {
  const providedField: BridgeApiTemplateField | undefined = rfiTemplate?.providedFields.find(
    (requestedField) => requestedField.name === field
  );

  const getValue = () => {
    switch (providedField?.type) {
      case BridgeApiTemplateFieldTypeEnum.FILE:
        return value.name;
      case BridgeApiTemplateFieldTypeEnum.DATE:
        return dayjs(value).format('YYYY-MM-DD');
      default:
        return value;
    }
  };

  return (
    <>
      <Col className='display-name'>{providedField?.displayName ? providedField?.displayName : field}:</Col>
      <Col className='values'>{getValue()}</Col>
    </>
  );
};

export interface JsonMessageFileValue {
  id: number;
  fileName: string;
}

export const onDownloadAttachment = async (attachmentHandle: string, fileName: string): Promise<void> => {
  const attachmentBlob = await attachmentApi.downloadAttachment({
    attachmentId: attachmentHandle,
  });
  triggerDownload(attachmentBlob, fileName);
};

export const triggerDownload = (blob: Blob, fileName: string): void => {
  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', fileName);

  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }

  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  window.URL.revokeObjectURL(blobURL);
};

export function isNonEmptyValue(value: any): boolean {
  return value !== undefined && value !== null && value !== '' && value.length !== 0;
}

export const ACCEPTED_EXTENSION_TYPES = {
  'image/png': ['.png', '.jpg', '.gif', '.jpeg'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/zip': ['.zip', '.bdoc', '.ddoc'],
  'application/vnd.etsi.asic-e+zip': ['.asice'],
  'text/plain': ['.txt', '.csv'],
  'text/csv': ['.csv'],
  'application/xml': ['.xml'],
};

const ANONYMOUS_ORGANISATION = 'Anonymous organisation';

export function getNameForCounterparty(
  messageType: InternalApiAlertRfiMessageType,
  counterparty: {
    userName: string | undefined;
    organisationName: string | undefined;
  },
  counterparties: BridgeApiConversationCounterparty[]
): string | undefined {
  if (messageType === InternalApiAlertRfiMessageType.SENT) {
    return counterparty.userName || counterparty.organisationName;
  }

  if (counterparty.organisationName?.startsWith('alias-')) {
    const anonymousCounterparties = counterparties
      .filter((cp) => cp.organisationName?.startsWith('alias-'))
      .sort((a, b) => a.organisationName!.localeCompare(b.organisationName!));

    if (anonymousCounterparties.length > 1) {
      const counterpartyId = anonymousCounterparties.findIndex(
        (cp) => cp.organisationName === counterparty.organisationName
      );
      return ANONYMOUS_ORGANISATION + ' ' + (counterpartyId + 1);
    }

    return ANONYMOUS_ORGANISATION;
  }

  return [counterparty.userName, counterparty.organisationName].filter((val) => val).join(' • ');
}

export function constructValidationSchema(fields: Array<BridgeApiTemplateField>, requestedAttributes: string[]) {
  return Yup.object().shape(
    {
      ...fields
        .filter((templateField) => requestedAttributes.includes(templateField.name))
        .reduce((obj, templateField) => {
          const fieldConf = getMessengerFieldConfiguration(templateField);
          const showWhenConf = fieldConf.show_when;
          const repeatable = fieldConf.repeatable || false;

          let fieldSchema = getSchemaType(templateField);

          if (repeatable) {
            fieldSchema = Yup.array()
              .min(templateField.required ? 1 : 0)
              .of(setRequired(fieldSchema, templateField));
          }

          if (showWhenConf) {
            fieldSchema = fieldSchema.when(showWhenConf.field_id, {
              is: showWhenConf.field_value,
              then: (schema: Yup.AnySchema) => setRequired(schema, templateField),
            });
          } else {
            fieldSchema = setRequired(fieldSchema, templateField);
          }

          return { ...obj, [templateField.name]: fieldSchema };
        }, {}),
      responseComment: Yup.string().test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value)),
    },
    []
  );
}

export function getDependentFields(template: BridgeApiTemplate) {
  return template.requestedFields
    .filter((field) => getMessengerFieldConfiguration(field).show_when !== undefined)
    .reduce((depFieldsMap: { [p: string]: BridgeApiTemplateField[] }, x) => {
      const showWhenConfiguration = getMessengerFieldConfiguration(x).show_when!;
      (depFieldsMap[showWhenConfiguration.field_id] = depFieldsMap[showWhenConfiguration.field_id] || []).push(x);
      return depFieldsMap;
    }, {});
}
